import { useMemo, useState, type ReactNode } from 'react';
import { ArrowUpRight, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

import { initialsOf } from '../../../lib/bureau/avatar';
import { normalizeLogoUrl, resolveCompanyLogo } from '../../../lib/companyLogo';
import type { CRMAccount } from '../../../types/crm';
import { clientBadgeStyle, type ClientBadge } from '../../../utils/clientStatus';
import { BTN_GHOST, BTN_PRIMARY } from '../../bureau/buttons';
import { REF } from '../../bureau/encre';

/** The role's dot: green a client, grey a former one, the waiting amber a prospect. */
const DOT: Record<ClientBadge['kind'], string> = {
  client: 'bg-emerald-500',
  former: 'bg-desk-faint',
  prospect: 'bg-desk-signal',
};

const hostOf = (website: string): string => website.replace(/^https?:\/\//iu, '').replace(/\/$/u, '');
const hrefOf = (website: string): string => (/^https?:\/\//iu.test(website) ? website : `https://${website}`);

/**
 * The head of an account's sheet (mock-up « Fiche »): the logo (its own, else its site's, as
 * everywhere: `resolveCompanyLogo`), else — or when it fails to load — the initials on Encre's disc;
 * the name; who it is for us (client, former client, prospect — `v_account_role`), its town and its
 * site; then the gestures — call (the app's call button, when it has a number), give Otto a mission
 * about it (the blue: the gesture that moves things forward), open the full record.
 */
export function AccountSheetHeader({
  account,
  badge,
  call,
  newMissionHref,
  ficheHref,
  onNavigate,
}: {
  account: CRMAccount;
  badge: ClientBadge | null;
  /** The call button for the account's number, when it has one and the user may call. */
  call: ReactNode;
  newMissionHref: string;
  ficheHref: string;
  /** Leaving the sheet for a page closes it. */
  onNavigate: () => void;
}) {
  const role = badge ? clientBadgeStyle(badge).label : null;
  const logo = useMemo(
    () => resolveCompanyLogo({ logoUrl: normalizeLogoUrl(account.logo_url), website: account.website }),
    [account.logo_url, account.website],
  );
  const [logoFailed, setLogoFailed] = useState<string | null>(null);
  return (
    <header className="flex flex-col gap-5 pb-6">
      <div className="flex items-center gap-4">
        {logo && logoFailed !== logo ? (
          <img
            src={logo}
            alt=""
            onError={() => setLogoFailed(logo)}
            className="h-14 w-14 shrink-0 rounded-2xl bg-white object-contain p-1.5 ring-1 ring-desk-line"
          />
        ) : (
          <span
            aria-hidden="true"
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-desk-selected text-base font-medium text-desk-ink"
          >
            {initialsOf(account.name)}
          </span>
        )}
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-medium leading-8 tracking-[-0.02em] text-desk-ink">{account.name}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-desk-soft">
            {badge && role && (
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden="true" className={`h-2 w-2 rounded-full ${DOT[badge.kind]}`} />
                {role}
              </span>
            )}
            {account.city && <span>{account.city}</span>}
            {account.website && (
              <a href={hrefOf(account.website)} target="_blank" rel="noopener noreferrer" className={REF}>
                {hostOf(account.website)}
              </a>
            )}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {call}
        <Link to={newMissionHref} onClick={onNavigate} className={BTN_PRIMARY}>
          <Plus size={16} aria-hidden="true" /> Nouvelle mission
        </Link>
        <Link to={ficheHref} onClick={onNavigate} className={BTN_GHOST}>
          Fiche complète <ArrowUpRight size={16} aria-hidden="true" />
        </Link>
      </div>
    </header>
  );
}
